import { Directive, forwardRef, input } from '@angular/core';
import { AbstractControl, NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';

@Directive({
  selector: '[appFutureDateValidator]',
  exportAs: 'appFutureDateValidator',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => FutureDateValidatorDirective),
      multi: true
    }
  ]
})
export class FutureDateValidatorDirective implements Validator {
  /**
   * When true, compares only local calendar dates (midnight-to-midnight).
   * When false (default), compares full date-time timestamps.
   */
  readonly dateOnly = input<boolean>(false);

  /**
   * When true, allows the value to be equal to today/now.
   * When false (default), the value must be strictly in the future.
   */
  readonly inclusive = input<boolean>(false);

  // Validates a single date value against the current moment or current day.
  // dateOnly=true compares local calendar days; otherwise full timestamps are compared.
  // inclusive=true allows equality with today/now, exclusive requires strictly after.
  validate(control: AbstractControl): ValidationErrors | null {
    const inputDate = this.toDate(control.value);

    if (!inputDate) {
      return null;
    }

    const now = new Date();
    const left = this.dateOnly() ? this.startOfDay(inputDate).getTime() : inputDate.getTime();
    const right = this.dateOnly() ? this.startOfDay(now).getTime() : now.getTime();
    const isInvalid = this.inclusive() ? left < right : left <= right;

    return isInvalid ? { futureDate: true } : null;
  }

  // Converts supported control values into a valid Date instance.
  // Returns null when the value is missing or cannot be parsed.
  private toDate(value: unknown): Date | null {
    let parsedDate: Date | null = null;

    if (value instanceof Date) {
      parsedDate = Number.isNaN(value.getTime()) ? null : value;
    } else if (typeof value === 'string' || typeof value === 'number') {
      const candidate = new Date(value);
      parsedDate = Number.isNaN(candidate.getTime()) ? null : candidate;
    }

    return parsedDate;
  }

  // Normalizes a date to local midnight for date-only comparisons.
  private startOfDay(value: Date): Date {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
}
